import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Logo from 'assets/images/branding/logo.svg';
import { useNavigate } from 'react-router-dom';
import {
	Accordion,
	Button,
	ButtonStack,
	ChartLegend,
	DataTable,
	Drawer,
	ExportButton,
	FilterFields,
	LogoOverlay,
	PageHeading,
	Panel,
	PrintableReportHeader,
	Select,
} from 'components';
import { useAppSelector, useSystem, useGetParLevelInventoryDetailsQuery, useGetParLevelInventoryQuery } from 'store';
import { useFilters, useToast } from 'context';

import type { ParLevelInventoryData, ParLevelInventoryDetailItem, ParLevelInventoryDetailsResponse } from 'store';
import { NumberLocale } from 'yup/lib/locale';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryLabel, VictoryLine } from 'victory';
import { border, determineHeight, fontFamily, getColor } from 'utils';
import { StringMatcher } from 'cypress/types/net-stubbing';
import { useSearchParams } from 'react-router-dom';

const viewByOptions = [
	{
		label: 'Item',
		value: 'item',
	},
	{
		label: 'Area',
		value: 'area',
	},
	{
		label: 'Service Line',
		value: 'service_line',
	},
];

export function ParLevelInventory() {
	const { selectedFacility, selectedSystem } = useAppSelector((state) => state.userState);
	const { data: systemData } = useSystem();
	const facilities = systemData?.facilities;
	const currentFacility = facilities?.find((f) => f.id === selectedFacility);
	const system = systemData?.healthsystems.find((h) => h.id === selectedSystem);
	const [printing, setPrinting] = useState(false);
	const {
		dateRange,
		daysOfWeek,
		serviceLines,
		encounterTypes,
		rooms,
		procedures,
		primetime,
		addOns,
		dropDowns,
		parLocations,
		manufacturers,
		saveDropdown,
		resetFilters,
		applyFilters,
		clearFilters,
		filtersAreDirty,
		currentPageLoaded,
		metadata,
		filtersAreFetching,
	} = useFilters();
	const { createToast } = useToast();

	// There is sometimes a delay in our filters when a user switches pages
	// (which is why we check if currentPageLoaded is equal to our current page),
	// To account for the delay, we tell our RTK Query to skip until we set skipRequest to false.
	const [skipRequest, setSkipRequest] = useState(true);
	useEffect(() => {
		setTimeout(() => {
			if (currentPageLoaded === '/par-level-inventory') {
				setSkipRequest(false);
			}
		}, 0);
	}, [currentPageLoaded]);

	const [selectedViewBy, setSelectedViewBy] = useState(viewByOptions[0]);

	const {
		data: parLevelInventoryData,
		error,
		isFetching: parLevelInventoryFetching,
	} = useGetParLevelInventoryQuery(
		{
			facility_id: selectedFacility,
			filters: {
				days_of_week: daysOfWeek?.applied,
				start_date: format(dateRange?.applied.startDate, 'M/d/yyyy'),
				end_date: format(dateRange?.applied.endDate, 'M/d/yyyy'),
				par_locations: parLocations?.applied,
				manufacturers: manufacturers?.applied,
			},
		},
		{
			skip: skipRequest || filtersAreFetching,
		}
	);

	const exportData = [];
	// if (parLevelInventoryData?.data) {
	// 	exportData = parLevelInventoryData.data
	// 		.map((group) => {
	// 			const items = group.values.map((v) => {
	// 				// we need any for exporting here
	// 				// eslint-disable-next-line
	// 				const row: any = {
	// 					value: v.y,
	// 				};
	// 				const key = selectedOverviewViewBy.value.replace('_id', '');
	// 				row[key] = v.x;
	// 				if (selectedOverviewGroupBy.value !== 'none') {
	// 					row[selectedOverviewGroupBy.value] = group.groupValue;
	// 				}
	// 				return row;
	// 			});

	// 			return items;
	// 		})
	// 		.flat();
	// }

	// Generate PDF from the current page, following the Analytics export approach.
	// If the captured report is taller than one PDF page, slice the same canvas
	// across as many PDF pages as needed instead of squeezing/clipping it.
	const generatePDF = async () => {
		const element = document.getElementById('overview');

		if (!element) {
			setPrinting(false);
			return;
		}

		try {
			const doc = new jsPDF();

			const html2canvasOptions = {
				useCORS: true,
				allowTaint: true,
				dpi: 200,
				scrollY: -window.scrollY,
			};

			const canvas = await html2canvas(element, html2canvasOptions);
			const pageWidth = doc.internal.pageSize.getWidth();
			const pageHeight = doc.internal.pageSize.getHeight();
			const margin = 10;
			const imageWidth = pageWidth - margin * 2;
			const imageHeight = (canvas.height * imageWidth) / canvas.width;
			const pageContentHeight = pageHeight - margin * 2;
			const pageCanvasHeight = (pageContentHeight * canvas.width) / imageWidth;
			let renderedHeight = 0;
			let pageIndex = 0;

			while (renderedHeight < canvas.height) {
				const sliceHeight = Math.min(pageCanvasHeight, canvas.height - renderedHeight);
				const pageCanvas = document.createElement('canvas');
				const pageContext = pageCanvas.getContext('2d');

				pageCanvas.width = canvas.width;
				pageCanvas.height = sliceHeight;

				if (!pageContext) {
					break;
				}

				pageContext.drawImage(canvas, 0, renderedHeight, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

				const imageData = pageCanvas.toDataURL('image/jpeg');
				const sliceImageHeight = Math.min(imageHeight - pageIndex * pageContentHeight, pageContentHeight);

				if (pageIndex > 0) {
					doc.addPage();
				}

				doc.addImage(imageData, 'JPEG', margin, margin, imageWidth, sliceImageHeight);

				renderedHeight += sliceHeight;
				pageIndex += 1;
			}

			doc.save(`flip_rooms_${currentFacility?.name ?? 'report'}.pdf`);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error generating PDF:', error); // keeping for easy error troubleshooting
		} finally {
			setPrinting(false);
		}
	};

	// Used to hide certain elements printing purposes
	const [searchParams] = useSearchParams();
	const printable = searchParams.get('printable') === 'true';
	const data: ParLevelInventoryData[] = parLevelInventoryData?.data ?? [];

	return (
		<div className='relative overflow-hidden' id='overview'>
			{printing && (
				<PrintableReportHeader>
					<p className='text-[2em] font-bold leading-tight text-bluegray-900'>Charge Reconciliation</p>
					<p className='mt-2 text-[1.35em] font-medium text-bluegray-500'>{`${format(
						dateRange.applied.startDate,
						'M/d/yyyy'
					)} – ${format(dateRange.applied.endDate, 'M/d/yyyy')}`}</p>
					<p className='mt-1 text-[1.1em] font-medium text-bluegray-500'>{`${
						currentFacility?.intraop_facility_name_alias ? currentFacility.intraop_facility_name_alias : currentFacility?.name
					} - ${system?.name}`}</p>
				</PrintableReportHeader>
			)}
			{!printing && <PageHeading>Par Level Inventory Overview</PageHeading>}
			<div className='relative'>
				{(parLevelInventoryFetching || filtersAreFetching || skipRequest) && <LogoOverlay backgroundColor={'white'} />}
				<Panel
					title={'Par Level Inventory Overview'}
					tooltipContent={
						!printable && !printing
							? 'Use this visualization to determine on-time start performance across various categories, like time, surgeon, or room. In general, higher on time start performance correlates with higher intra-day schedule adherence.'
							: undefined
					}
					goToHelpID='timelystarts'
					headerContentRight={
						!printable && !printing ? (
							<>
								<div className='flex justify-end pb-3'>
									<Drawer
										metadata={metadata}
										filtersAreDirty={filtersAreDirty}
										trigger={
											<Button sizeX='sm' sizeY='md' variant={'primary-ghost'} className='mr-2'>
												<span className='material-symbol'>filter_alt</span>
												Filters
											</Button>
										}
										quickActions={[
											{
												icon: 'undo',
												onClick: resetFilters,
												tooltipText: 'Discard unapplied filter changes',
												disabled: !filtersAreDirty,
											},
											{
												icon: 'restart_alt',
												onClick: clearFilters,
												tooltipText: 'Reset filters to default',
												disabled: !metadata.saved_at,
											},
										]}
										actionButtons={[
											{
												onClick: applyFilters,
												children: 'Apply',
												disabled: !filtersAreDirty,
											},
											{
												// eslint-disable-next-line @typescript-eslint/no-empty-function
												onClick: () => {},
												children: 'Views',
												disabled: false,
											},
										]}
									>
										<FilterFields fields={['dateRange', 'daysOfWeek']} />
									</Drawer>
									<div>
										<ButtonStack>
											<Button sizeX='md' sizeY='md' variant='primary'>
												<ExportButton no_button={true} sizeX='md' sizeY='md' variant='primary' contents={[]}>
													Export CSV
												</ExportButton>
											</Button>

											<Button
												sizeX='md'
												sizeY='md'
												variant='primary'
												onClick={() => {
													setPrinting(true);
													setTimeout(() => {
														generatePDF();
													}, 0);
												}}
											>
												Export PDF
											</Button>
										</ButtonStack>
									</div>
								</div>
							</>
						) : printable ? (
							<div className='flex text-p1 w-screen justify-between' id='header'>
								<div className='flex text-center w-1/5'>
									<div className='pt-5 pl-8'>
										<img className='h-11 w-11 relative -left-[2px]' src={Logo} alt='Merlin' />
									</div>
									<p className='pt-5 mt-1 pl-3 w-16 whitespace-nowrap text-h5 font-semibold'>FCOTs Overview</p>
								</div>

								<div className='flex flex-row justify-end w-3/4 mt-2 pr-4'>
									<div className='px-4 py-2 bg-blue-200 w-fit rounded-md self-center mr-6'>
										{`${format(dateRange.applied.startDate, 'M/d/yyyy')} - ${format(dateRange.applied.endDate, 'M/d/yyyy')}`}
									</div>
									<div className='px-4 py-2 bg-blue-200 w-fit rounded-md self-center'>
										{`${
											currentFacility?.intraop_facility_name_alias
												? currentFacility.intraop_facility_name_alias
												: currentFacility?.name
										} - ${system?.name}`}
									</div>
								</div>
							</div>
						) : undefined
					}
					isEmpty={parLevelInventoryData && parLevelInventoryData.data?.length === 0}
					className={`${printable && 'border-none pb-3 text-h4'}`}
				>
					<div className='max-w-screen-2xl m-auto'>
						{data.map((group, i) => {
							return <ParLevelBarChart key={i} values={group.values} />;
						})}
					</div>
				</Panel>
			</div>
		</div>
	);
}

export interface ParLevelBarChartsProps {
	values: { x: string; y: number; id: number }[];
}

export function ParLevelBarChart({ values }: ParLevelBarChartsProps) {
	const axisFontSize = 5;
	const valuesAxis = {
		axis: { stroke: border },
		tickLabels: { fontFamily: fontFamily, fontSize: axisFontSize, padding: 5 },
	};

	const viewByAxis = {
		axis: { stroke: border },
		grid: { stroke: border },
		tickLabels: { fontFamily: fontFamily, fontSize: axisFontSize, padding: 5 },
	};

	const dataMaxY = (values && Math.ceil(Math.max(...values.map((d) => d.y)) + 50000)) ?? 100000;
	const avg = Math.round(values.reduce((r, c) => r + c.x.toString().length, 0) / values.length);
	const regx = new RegExp('.{1,avg}(?:\\s|$)'.replace('avg', avg > 20 ? (avg - 2).toString() : '18'), 'g');
	const navigate = useNavigate();
	function truncateString(str: string, maxLength: number): string {
		return str.length > maxLength ? str.slice(0, maxLength) : str;
	}

	return (
		<VictoryChart
			height={determineHeight(values.length)}
			padding={{ top: 15, bottom: 15, left: avg > 20 ? avg * 3 : 65, right: 10 }}
			domain={{ y: [0, dataMaxY < 100 ? 100 : dataMaxY] }}
			horizontal
			domainPadding={10}
		>
			<VictoryAxis offsetY={12} dependentAxis style={viewByAxis} tickFormat={(t) => `$${t}`} />
			<VictoryAxis style={valuesAxis} tickFormat={(t) => `${truncateString(t, 20)}`} />

			<VictoryBar
				data={values}
				// we noticed VictoryBar was not preserving the sort order from the endpoint so we are sorting values here
				// we are sorting values one way in the backend and sorting again here...maybe we can get around this behavior in the future
				sortKey='x'
				sortOrder='descending'
				cornerRadius={2}
				barWidth={12.5}
				horizontal
				style={{
					data: {
						fill: ({ datum }) => {
							return getColor('blue');
						},
					},
					labels: {
						fontFamily,
						fontSize: 6,
					},
				}}
				labels={values.map((v) => `$${v.y.toLocaleString()}`)}
				events={[
					{
						target: 'data',
						eventHandlers: {
							onClick: (event, { datum }: { datum: { x: string; y: number; id: number } }) => {
								navigate(
									`/par-level-inventory/item-details?area=${encodeURIComponent(datum.x)}&id=${encodeURIComponent(datum.id)}`
								);
								return [];
							},
						},
					},
				]}
			/>
		</VictoryChart>
	);
}

export default ParLevelInventory;
